/**
 * Pagina Billing — stato abbonamento + piani disponibili + upgrade Lemon Squeezy.
 *
 * Sorgente dati: tabella `subscriptions` letta via useSubscription.
 *
 * Flusso upgrade:
 *  1. User clicca "Passa a X" su piano monthly/yearly
 *  2. useCreateCheckout chiama edge function `lemonsqueezy-create-checkout`
 *  3. Edge function crea checkout LS via API → ritorna URL hosted
 *  4. Frontend redirect a `url` (LS checkout)
 *  5. Dopo pagamento, LS rimanda a `/settings/billing?upgrade=success`
 *  6. Webhook LS aggiorna `subscriptions` (status, plan_code, period_end)
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  CheckCircle2, Clock, AlertTriangle, Crown, ExternalLink, Loader2, Sparkles,
  CreditCard, Settings as SettingsIcon, FileText,
} from 'lucide-react';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from '@/components/layout/AppSidebar';
import Header from '@/components/layout/Header';
import { useSubscription, type PlanCode } from '@/hooks/useSubscription';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { useCreateCheckout, type Plan, type BillingCycle } from '@/hooks/useCreateCheckout';
import { useCustomerPortal } from '@/hooks/useCustomerPortal';
import { toast } from 'sonner';
import { formatNumber } from '@/utils/format';

interface PlanDef {
  code: PlanCode;
  name: string;
  /** Prezzo mensile in € (es. 19). null per il trial. */
  priceMonthly: number | null;
  /** Prezzo annuale totale in € (es. 182, equivalente €15.17/mese). null per il trial. */
  priceYearly: number | null;
  features: string[];
}

const PLANS: PlanDef[] = [
  {
    code: 'trial',
    name: 'Trial',
    priceMonthly: null,
    priceYearly: null,
    features: ['Tutto incluso', '1 utente', 'Nessuna carta richiesta', 'Durata: 7 giorni'],
  },
  {
    code: 'solo',
    name: 'Solo',
    priceMonthly: 19,
    priceYearly: 182,
    features: ['Progetti illimitati', 'Listini fornitori', 'Preventivi & PDF', 'Configuratore V2'],
  },
  {
    code: 'studio',
    name: 'Studio',
    priceMonthly: 49,
    priceYearly: 470,
    features: ['Tutto del Solo', 'Vista 3D fotorealistica', 'CRM & agenda', 'Mappa clienti', 'Supporto prioritario'],
  },
  {
    code: 'team',
    name: 'Team',
    priceMonthly: 149,
    priceYearly: 1430,
    features: ['Tutto dello Studio', 'Fino a 10 utenti', 'Ruoli granulari', 'Audit log', 'API access'],
  },
];

const STATUS_LABEL: Record<string, { label: string; className: string }> = {
  trialing: { label: 'In prova', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  active: { label: 'Attivo', className: 'bg-green-50 text-green-700 border-green-200' },
  past_due: { label: 'Pagamento in ritardo', className: 'bg-red-50 text-red-700 border-red-200' },
  canceled: { label: 'Cancellato', className: 'bg-zinc-50 text-zinc-700 border-zinc-200' },
  expired: { label: 'Scaduto', className: 'bg-red-50 text-red-700 border-red-200' },
};

function euro(v: number): string {
  return `€${formatNumber(v, 0)}`;
}

const Billing: React.FC = () => {
  const { subscription, isTrialing, trialDaysLeft, daysUntilRenewal, isLoading } = useSubscription();
  const { currentRole } = useCurrentOrganization();
  const canBill = currentRole === 'owner';
  const { createCheckout, isCreatingCheckout } = useCreateCheckout();
  const { openCustomerPortal, openUpdatePayment, isLoading: isPortalLoading } = useCustomerPortal();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Post-checkout redirect: se torniamo con ?upgrade=success il webhook LS può
  // ancora non aver scritto in DB (delay ~1-5s tra "ok pagamento" e ricezione
  // webhook). Invalida subito + ritenta dopo 3s e 7s per coprire i casi lenti:
  //  - subscription → ricarica piano + status
  //  - memberships → ricarica organizations.plan (single source of truth UI)
  //  - plan-usage → counter X/limite si aggiornano (su Studio diventano ∞)
  useEffect(() => {
    if (searchParams.get('upgrade') === 'success') {
      toast.success('Pagamento completato!', {
        description: 'Sincronizzazione piano in corso…',
        duration: 7000,
      });

      const refreshPlan = () => {
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
        queryClient.invalidateQueries({ queryKey: ['memberships'] });
        queryClient.invalidateQueries({ queryKey: ['plan-usage'] });
      };
      refreshPlan();
      const t1 = setTimeout(refreshPlan, 3000);
      const t2 = setTimeout(refreshPlan, 7000);

      // Rimuovi il param dall'URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('upgrade');
      setSearchParams(newParams, { replace: true });

      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [searchParams, setSearchParams, queryClient]);

  const currentPlan = subscription?.plan_code ?? 'trial';
  const currentCycle = subscription?.billing_cycle as BillingCycle | undefined;
  const status = subscription?.status ?? 'trialing';
  const statusInfo = STATUS_LABEL[status] ?? STATUS_LABEL.trialing;
  // Subscription bloccata (cancelled / past_due / expired): l'utente DEVE poter
  // riattivare lo stesso piano. In stato attivo invece blocchiamo "stesso piano".
  const isSubscriptionBlocked = status === 'canceled' || status === 'past_due' || status === 'expired';
  // Customer Portal visibile solo se esiste subscription LS reale (no trial puro).
  // Su trial l'utente non ha ancora una sub LS → portal restituirebbe 404.
  const hasLsSubscription = !!subscription?.lemonsqueezy_subscription_id;

  const handleUpgrade = async (target: PlanCode) => {
    if (target === 'trial') {
      toast.info('Il piano Trial non è acquistabile (è quello di default)');
      return;
    }
    // Se la subscription è bloccata, permettiamo SEMPRE il checkout sullo stesso
    // piano (= riattivazione). Altrimenti blocchiamo se l'utente è già su quel
    // piano+ciclo per evitare doppi pagamenti accidentali.
    if (!isSubscriptionBlocked && target === currentPlan && currentCycle === billingCycle) {
      toast.info('Sei già su questo piano e ciclo di fatturazione');
      return;
    }
    await createCheckout(target as Plan, billingCycle);
    // Redirect handled inside the hook.
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <SidebarInset className="flex flex-col">
          <Header />
          <main className="flex-1 p-6 md:p-8 space-y-6 max-w-5xl">
            <div className="flex items-center gap-3">
              <Crown className="h-7 w-7 text-primary" />
              <div>
                <h1 className="text-2xl font-bold tracking-tight" data-tour-id="billing-page">Abbonamento & fatturazione</h1>
                <p className="text-muted-foreground text-sm">
                  Stato del piano attivo e upgrade. Visibile solo al titolare dell'organizzazione.
                </p>
              </div>
            </div>

            {!canBill && (
              <Card>
                <CardContent className="py-6 text-center text-sm text-muted-foreground">
                  Solo il titolare dell'organizzazione può modificare l'abbonamento.
                </CardContent>
              </Card>
            )}

            {/* Stato attuale */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Stato attuale</span>
                  <Badge variant="outline" className={statusInfo.className}>
                    {statusInfo.label}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {isLoading ? 'Caricamento…' : 'Informazioni aggiornate dall\'ultimo evento di pagamento.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Piano</p>
                  <p className="text-lg font-bold capitalize">
                    {currentPlan}
                    {currentCycle && (
                      <span className="text-xs font-normal text-muted-foreground ml-2">
                        ({currentCycle === 'yearly' ? 'annuale' : 'mensile'})
                      </span>
                    )}
                  </p>
                </div>
                {isTrialing && trialDaysLeft != null && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Trial</p>
                    <p className="text-lg font-bold inline-flex items-center gap-1">
                      <Clock className="h-4 w-4 text-amber-600" />
                      {trialDaysLeft} giorni rimasti
                    </p>
                  </div>
                )}
                {!isTrialing && daysUntilRenewal != null && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Rinnovo</p>
                    <p className="text-lg font-bold">
                      {daysUntilRenewal === 0 ? 'oggi' : `tra ${daysUntilRenewal} gg`}
                    </p>
                  </div>
                )}
                {status === 'past_due' && (
                  <div className="col-span-full rounded-md border border-red-300 bg-red-50 p-3 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-700 shrink-0" />
                    <span className="text-sm text-red-700">
                      Pagamento in ritardo. Aggiorna il metodo di pagamento o l'organizzazione passerà in stato Scaduto.
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Gestisci abbonamento — Customer Portal Lemon Squeezy.
                Visibile solo se esiste una sub LS reale (subscription_id valorizzato).
                Sul Trial puro non mostriamo nulla: non c'è ancora nessun pagamento da gestire. */}
            {canBill && hasLsSubscription && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <SettingsIcon className="h-4 w-4 text-muted-foreground" />
                    Gestisci abbonamento
                  </CardTitle>
                  <CardDescription>
                    Cambia metodo di pagamento, scarica fatture passate o annulla l'abbonamento dal portale Lemon Squeezy.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    className="justify-start h-auto py-3"
                    onClick={() => openUpdatePayment()}
                    disabled={isPortalLoading}
                  >
                    {isPortalLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4 mr-2 text-primary shrink-0" />
                    )}
                    <div className="flex flex-col items-start text-left">
                      <span className="font-medium text-sm">Aggiorna metodo di pagamento</span>
                      <span className="text-[11px] text-muted-foreground">
                        Cambia carta o aggiungi PayPal/Apple Pay
                      </span>
                    </div>
                  </Button>
                  <Button
                    variant="outline"
                    className="justify-start h-auto py-3"
                    onClick={() => openCustomerPortal()}
                    disabled={isPortalLoading}
                  >
                    {isPortalLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4 mr-2 text-primary shrink-0" />
                    )}
                    <div className="flex flex-col items-start text-left">
                      <span className="font-medium text-sm">Portale completo (fatture, annulla)</span>
                      <span className="text-[11px] text-muted-foreground">
                        Scarica fatture · annulla abbonamento
                      </span>
                    </div>
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Piani disponibili */}
            <div>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                <h2 className="text-lg font-semibold">Cambia piano</h2>
                <Tabs value={billingCycle} onValueChange={(v) => setBillingCycle(v as BillingCycle)}>
                  <TabsList>
                    <TabsTrigger value="monthly">Mensile</TabsTrigger>
                    <TabsTrigger value="yearly" className="gap-1.5">
                      Annuale
                      <Badge variant="secondary" className="bg-green-100 text-green-700 text-[10px] px-1.5">
                        <Sparkles className="h-2.5 w-2.5 mr-0.5" />
                        -20%
                      </Badge>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {PLANS.map((p) => {
                  const isCurrent = p.code === currentPlan && (
                    p.code === 'trial' || currentCycle === billingCycle
                  );
                  const isTrial = p.code === 'trial';
                  // Prezzo da mostrare in base al ciclo
                  const price = billingCycle === 'yearly' ? p.priceYearly : p.priceMonthly;
                  const periodLabel = billingCycle === 'yearly' ? '/anno' : '/mese';
                  const effectiveMonthly = billingCycle === 'yearly' && p.priceYearly
                    ? formatNumber(p.priceYearly / 12)
                    : null;

                  return (
                    <Card
                      key={p.code}
                      className={`relative flex flex-col ${isCurrent ? 'border-primary ring-2 ring-primary/20' : ''}`}
                    >
                      {isCurrent && (
                        <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                          Piano corrente
                        </Badge>
                      )}
                      <CardHeader>
                        <CardTitle className="text-lg">{p.name}</CardTitle>
                        <div>
                          {isTrial ? (
                            <span className="text-2xl font-bold">Gratis</span>
                          ) : (
                            <>
                              <span className="text-2xl font-bold">{price != null ? euro(price) : '—'}</span>
                              <span className="text-xs text-muted-foreground"> {periodLabel}</span>
                              {effectiveMonthly && (
                                <div className="text-[11px] text-green-700 font-medium mt-0.5">
                                  €{effectiveMonthly}/mese effettivi
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="flex-1 flex flex-col">
                        <ul className="space-y-1.5 flex-1 text-xs">
                          {p.features.map((f, i) => (
                            <li key={i} className="flex items-start gap-1.5">
                              <CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                        <Button
                          className="mt-4"
                          // Riattiva: variante destructive (rossa) per richiamare attenzione
                          variant={
                            isCurrent && isSubscriptionBlocked ? 'destructive' :
                            isCurrent ? 'outline' : 'default'
                          }
                          // Disable: piano corrente attivo, o utente non owner, o trial, o checkout in corso.
                          // NON disabilitiamo il piano corrente se la subscription è bloccata (deve poter riattivare).
                          disabled={
                            (isCurrent && !isSubscriptionBlocked) ||
                            !canBill ||
                            isTrial ||
                            isCreatingCheckout
                          }
                          onClick={() => handleUpgrade(p.code)}
                        >
                          {isCreatingCheckout ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                              Apertura checkout…
                            </>
                          ) : isCurrent && isSubscriptionBlocked ? (
                            `Riattiva ${p.name}`
                          ) : isCurrent ? (
                            'Attivo'
                          ) : isTrial ? (
                            'Default'
                          ) : (
                            `Passa a ${p.name}`
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            <Card>
              <CardContent className="py-4 flex items-start gap-3">
                <ExternalLink className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="text-xs text-muted-foreground">
                  <strong>Pagamenti gestiti da Lemon Squeezy.</strong> Il provider gestisce IVA UE, fatturazione automatica
                  e metodi di pagamento (carta, PayPal, Apple Pay, Google Pay). Il pagamento avviene su pagina sicura
                  esterna; al ritorno troverai il piano attivato automaticamente.
                </div>
              </CardContent>
            </Card>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default Billing;
